import { configureApi, type KeyValueStore } from '@erm/api-client';

/**
 * The browser's half of the platform seam.
 *
 * `@erm/api-client` holds every rule the backend publishes; this file supplies
 * the three things that differ per platform. On a phone the same call is made
 * with the OS keychain instead of `localStorage`, which is why the seam exists.
 *
 * Both stores fail closed. A private window, cleared site data or a browser set
 * to block storage all surface as "no stored value", and the session then lives
 * only for this page — which is a worse experience, never a broken one.
 */
function webStore(storage: () => Storage): KeyValueStore {
  return {
    get: (key) => {
      try {
        return storage().getItem(key);
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        storage().setItem(key, value);
      } catch {
        // Blocked storage is not an error the user can act on.
      }
    },
    remove: (key) => {
      try {
        storage().removeItem(key);
      } catch {
        // Nothing to clear.
      }
    },
  };
}

export function configureWebApi(): void {
  configureApi({
    /*
      Either name, so one variable serves every deployment.

      The rest of this platform is Next and uses NEXT_PUBLIC_; Vite's own prefix
      is VITE_. Accepting both means the address is set once, under one name,
      and a console cannot end up pointed at nothing because the variable was
      spelled for the other bundler.
    */
    baseUrl:
      (import.meta.env['NEXT_PUBLIC_API_BASE_URL'] as string | undefined) ??
      (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ??
      '/api',
    secureStore: webStore(() => localStorage),
    sessionStore: webStore(() => sessionStorage),
  });
}
